import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { HR } from 'foundations-components/transitional/components/HR';
import { Link } from 'foundations-components/transitional/components/Link';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReviewWizardRatingInput, ReviewWizardTextInput } from 'trellis-ecosystem';
import { EcosystemReviewWizard } from '../../../components/wizard/EcosystemReviewWizard';
import { POSITIVE_RATING_MINIMUM } from '../../../constants/ReviewLimits';
import { PENDING } from '../../../constants/UnreviewableReason';
import { AppReviewWizardHeader } from './AppReviewWizardHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function getCompletedAnswerFields(review) {
  return Object.entries(review.content).filter(([field, value]) => field && !!value).map(([field]) => field);
}
export function AppReviewWizard({
  appIconUrl,
  appName,
  editedReview,
  initialRating,
  isPublic,
  offeringId,
  onClose,
  onSuccess,
  onTrackInteraction,
  supportLink,
  unreviewableReason
}) {
  const isEditing = !!editedReview;

  // Extract rating from URL query parameter if present
  const ratingFromQuery = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    const ratingParam = params.get('rating');
    if (ratingParam) {
      const rating = parseInt(ratingParam, 10);
      if (!isNaN(rating) && rating >= 1 && rating <= 5) {
        return rating;
      }
    }
    return undefined;
  }, []);

  // Use query param rating if present, otherwise use initialRating prop
  const effectiveInitialRating = ratingFromQuery !== null && ratingFromQuery !== void 0 ? ratingFromQuery : initialRating;
  let defaultReview;
  const emptyReview = {
    content: {},
    ratings: {
      OVERALL: effectiveInitialRating
    },
    tags: {}
  };
  try {
    const storedReview = JSON.parse(sessionStorage.getItem(`APP_${offeringId}_review`));
    // If rating query param is present, override sessionStorage rating
    if (ratingFromQuery !== undefined && storedReview) {
      defaultReview = Object.assign({}, storedReview, {
        ratings: Object.assign({}, storedReview.ratings, {
          OVERALL: ratingFromQuery
        })
      });
    } else {
      defaultReview = storedReview || emptyReview;
    }
  } catch (__error) {
    defaultReview = emptyReview;
  }
  const [overallRating, setOverallRating] = useState(defaultReview.ratings['OVERALL'] || undefined);
  const [completedAnswerFields, setCompletedAnswerFields] = useState(getCompletedAnswerFields(defaultReview));
  const [requireFields, setRequireFields] = useState(overallRating !== undefined && overallRating < POSITIVE_RATING_MINIMUM || !!defaultReview.content.TITLE || !!defaultReview.content.REVIEW || !!defaultReview.content.PROS || !!defaultReview.content.CONS);
  const validationSchema = {
    content: {
      TITLE: {
        required: requireFields
      },
      REVIEW: {
        required: requireFields
      }
    },
    ratings: {
      OVERALL: {
        required: true
      }
    }
  };
  const openReviewWizardTracked = useRef(false);
  useEffect(() => {
    if (!openReviewWizardTracked.current) {
      onTrackInteraction({
        action: isEditing ? 'open edit review wizard' : 'open review wizard',
        section: 'review'
      });
      openReviewWizardTracked.current = true;
    }
  }, [isEditing, onTrackInteraction]);
  const alertedAndTrackedUnreviewableReason = useRef(false);
  useEffect(() => {
    if (!isEditing && unreviewableReason && unreviewableReason !== `ReviewError.${PENDING}` && alertedAndTrackedUnreviewableReason.current === false) {
      onTrackInteraction({
        action: 'review wizard auto-dismissed',
        section: 'review',
        value: unreviewableReason
      });
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.error.${unreviewableReason}`
        }),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-reviews.error.ReviewError.GENERIC_UNREVIEWABLE"
        }),
        type: 'danger'
      });
      alertedAndTrackedUnreviewableReason.current = true;
    }
  }, [isEditing, onTrackInteraction, unreviewableReason]);
  const handleSuccess = useCallback(() => {
    onTrackInteraction({
      action: 'submit review',
      section: 'review'
    });
    sessionStorage.removeItem(`APP_${offeringId}_review`);
    onSuccess();
  }, [onSuccess, offeringId, onTrackInteraction]);
  const handleClose = useCallback(() => {
    onTrackInteraction({
      action: 'dismiss review wizard',
      section: 'review'
    });
    onClose();
  }, [onClose, onTrackInteraction]);
  const onChange = useCallback(review => {
    if (review.content.TITLE || review.content.REVIEW || review.content.PROS || review.content.CONS) {
      setRequireFields(true);
    } else {
      setRequireFields(review.ratings['OVERALL'] < POSITIVE_RATING_MINIMUM);
    }
    if (review.ratings['OVERALL'] !== overallRating) {
      onTrackInteraction({
        action: 'set overall rating',
        section: 'review',
        value: review.ratings['OVERALL']
      });
      setOverallRating(review.ratings['OVERALL']);
    }
    const answerFields = getCompletedAnswerFields(review);
    answerFields.forEach(field => {
      if (!completedAnswerFields.includes(field)) {
        onTrackInteraction({
          action: 'add review answer',
          section: 'review',
          value: field
        });
      }
    });
    setCompletedAnswerFields(answerFields);
    if (!isEditing) {
      sessionStorage.setItem(`APP_${offeringId}_review`, JSON.stringify(review));
    }
  }, [completedAnswerFields, isEditing, offeringId, onTrackInteraction, overallRating]);
  const WizardHeader = useMemo(() => () => /*#__PURE__*/_jsx(AppReviewWizardHeader, {
    appIconUrl: appIconUrl,
    appName: appName,
    isEditing: isEditing
  }), [appIconUrl, appName, isEditing]);
  if (isPublic || !isEditing && unreviewableReason) {
    return null;
  }
  return /*#__PURE__*/_jsx(EcosystemReviewWizard, {
    defaultReview: defaultReview,
    editedReview: editedReview,
    HeaderComponent: WizardHeader,
    offeringId: offeringId,
    onChange: onChange,
    onClose: handleClose,
    onSuccess: handleSuccess,
    ratingSection: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(View, {
        marginBlockEnd: "400",
        as: "span",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h5",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-ui-reviews.wizard.app.header"
          })
        }), supportLink && /*#__PURE__*/_jsx(FormattedJSXMessage, {
          elements: {
            Link
          },
          message: "marketplace-ui-reviews.wizard.app.subheader_jsx",
          options: {
            appName,
            external: true,
            href: supportLink,
            onClick: () => {
              onTrackInteraction({
                action: 'click support link',
                section: 'review'
              });
            }
          }
        })]
      }), /*#__PURE__*/_jsx(ReviewWizardRatingInput, {
        fieldKey: "OVERALL",
        label: unescapedText('marketplace-ui-reviews.wizard.app.labels.OVERALL')
      })]
    }),
    reviewType: "APP",
    requireEditReason: true,
    validationSchema: validationSchema,
    children: (!!overallRating || isEditing) && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(ReviewWizardTextInput, {
        fieldKey: "TITLE",
        label: unescapedText('marketplace-ui-reviews.wizard.app.labels.TITLE'),
        placeholder: unescapedText('marketplace-ui-reviews.wizard.app.placeholders.TITLE'),
        resize: false,
        rows: 1
      }), ['REVIEW', 'PROS', 'CONS'].map(axis => /*#__PURE__*/_jsx(ReviewWizardTextInput, {
        fieldKey: axis,
        label: unescapedText(`marketplace-ui-reviews.wizard.app.labels.${axis}`),
        placeholder: unescapedText(`marketplace-ui-reviews.wizard.app.placeholders.${axis}`, {
          appName
        })
      }, axis)), /*#__PURE__*/_jsx(HR, {
        marginBlockStart: "800"
      }), /*#__PURE__*/_jsx(ReviewWizardTextInput, {
        fieldKey: "FEEDBACK",
        label: unescapedText('marketplace-ui-reviews.wizard.app.labels.FEEDBACK'),
        placeholder: unescapedText('marketplace-ui-reviews.wizard.app.placeholders.FEEDBACK', {
          appName
        })
      })]
    })
  });
}